<template>
    <div :class="{'is-required': required}">
        <select
            :id="id"
            class="form-control"
            :placeholder="placeholder"
            :required="required ? true : false"
        ></select>
    </div>
</template>

<script>
    import "selectize";
    import Utilities from "@/assets/js/utilities.js";

    export default {
        name: "BaseSearch",
        props: {
            // the selected value in the searchbar
            value: [String, Number, Object, Array, Boolean],
            // the placeholder to display
            placeholder: {
                type: String,
                default: "Type to search"
            },
            // an array of selectable options
            options: {
                type: Array,
                required: false
            },
            // the property of the selected item to be used as its value (eg. ID)
            valueField: {
                type: String,
                required: true
            },
            // the property of the selected item to be used as its value (eg. ID)
            valueFieldIsNumber: {
                type: Boolean,
                default: true
            },
            // the property to be used as the display for each item
            labelField: {
                type: String,
                required: false
            },
            // an array of properties to be used when searching
            searchFields: {
                type: Array,
                required: true
            },
            // for use in a form - whether or not the field is required
            required: {
                type: Boolean,
                default: true
            },
            // function used to format options for display
            optionRender: {
                type: Function,
                required: false
            },
            // function used to format selected items for display
            itemRender: {
                type: Function,
                required: false
            },
            // the maximum number of items that can be selected (use null for unlimited)
            maxItems: {
                type: Number,
                default: 1
            },
            // disable the searchbar
            disabled: {
                type: Boolean,
                default: false
            },
            sortField: {
                type: [String, Array],
                default: null
            },
            createItems: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                id: null,
                searchbar: null,
                updateLatch: false
            };
        },
        watch: {
            options(options) {
                this.searchbar.clear()
                this.searchbar.clearOptions();
                this.addSearchField(options);
                this.searchbar.addOption(options);
                if (this.value) {
                    this.searchbar.addItem(this.value);
                } else {
                    this.searchbar.clear();
                }
            },
            value(value) {
                // if model value changes in parent, reflect change in searchbar
                if (!this.updateLatch) {
                    this.setValue(value);
                } else {
                    this.updateLatch = false;
                }
                
            },
            disabled() {
                this.checkDisabled();
            }
        },
        created() {
            this.id = this._uid;
        },
        mounted() {
            var vm = this;

            let options = {
                valueField: this.valueField,
                searchField: "selectizeSearch",
                create: this.createItems,
                maxItems: this.maxItems,
                maxOptions: 1000,
                highlight: false,
                preload: true,
                openOnFocus: true,
                onFocus: function() {},
                allowEmptyOption: true,
                onChange: function(value) {
                    if (Array.isArray(value) && vm.valueFieldIsNumber) {
                        // this is a hack - need to convert from strings to numbers because selectize converts elements to strings by default
                        vm.$emit("input", value.map(val => Number(val)));   
                    } else if (vm.valueFieldIsNumber) {
                        vm.$emit("input", Number(value));
                    } else {
                        vm.$emit("input", value);
                    }
                },
                render: {}
            };

            if (this.labelField) {
                options.labelField = this.labelField;
            }
            if (this.optionRender) {
                options.render.option = this.optionRender;
            }
            if (this.itemRender) {
                options.render.item = this.itemRender;
            }
            if (this.sortField) {
                options.sortField = this.sortField;
            }

            var $searchbar = $("#" + this.id).selectize(options);

            this.searchbar = $searchbar[0].selectize;
            this.searchbar.clear();

            this.checkDisabled();

            // set initial options
            if (this.options) {
                // create searchable string
                this.addSearchField(this.options);
                this.searchbar.addOption(this.options);
            }

            // set initial value
            this.setValue(this.value);
        },
        methods: {
            // adds a search field to the selectize option by concatenating search fields on each object
            addSearchField(options) {
                options.forEach(option => {
                    option.selectizeSearch = "";
                    this.searchFields.forEach(field => {
                        option.selectizeSearch =
                            option.selectizeSearch +
                            Utilities.getNestedProperty(field, option);
                    });
                });
            },
            // check if the searchbar should be disabled
            checkDisabled() {
                if (this.disabled) {
                    this.searchbar.disable();
                } else {
                    this.searchbar.enable();
                }
            },
            // set the value of the searchbar
            setValue(value) {
                // add each item individually if value is array
                if (Array.isArray(value) && value.length > 0) {
                    this.searchbar.clear(true);
                    value.forEach(item => {
                        this.searchbar.addItem(item, true);
                    });
                // add single item if number
                } else if (!Array.isArray(value) && value) {
                    this.searchbar.addItem(value);
                } else {
                    // clear searchbar if blank
                    this.searchbar.clear(true);
                }
                
            }
        }
    };
</script>

<style lang="scss">
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/selectize.scss";

.was-validated {
    .is-required {
        .invalid:not(.has-items) {
            border-color: $danger;
        }
    }
}

.selectize-control .selectize-input {
    &.disabled {
        background-color: $input-disabled-bg !important;
        opacity: 1.0 !important;
    }
    cursor: text !important;
    input {
        cursor: text !important;
    }
    color: $gray-800 !important;
}

.selectize-dropdown {
    border: $dropdown-border-width solid $dropdown-border-color !important;
    border-radius: $dropdown-border-radius !important;
    box-shadow: $box-shadow-sm !important;
}

.selectize-dropdown-content {
    .option {
        cursor: pointer !important;
    }
    font-size: 15px;
}

.selectize-control.single .selectize-input:after {
    display: none !important;
}
</style>
